import IconFont from '@meitu/xdesign-icon-font';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { transformLowLeverTextDataToCss } from '@/utils/transform-kit';
import { MaterialIdsType, type ILayerBehavior, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { EdgelessTextLayer } from '../edgeless';
import { ShapeTextRenderer } from '../edgeless/shape-text-renderer';
import { filterSpecialChars } from '../utils';

type Behaviors = Pick<
    ILayerBehavior,
    'getEdgelessObject' | 'getMenuOverlapName' | 'getMenuOverlapIcon' | 'onBeforeAddOrUpdate'
>;

export const createRendererBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        getEdgelessObject(layer: ILayerProps) {
            const framework = getFramework(identity);
            const shapeTextRenderer =
                framework.service.registration.getInstance<ShapeTextRenderer>('ShapeTextRenderer');
            if (!shapeTextRenderer) throw new Error('ShapeTextRenderer is not registered.');
            return new EdgelessTextLayer(layer.id, framework, shapeTextRenderer);
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();

            const layerData = layer.data as ITextLayerProps;
            return layerData.text || $t((v) => v.layers.text);
        },

        getMenuOverlapIcon(_: ILayerProps) {
            return <IconFont name="TextSquare" fontSize="1em" />;
        },

        /**
         * 添加文本图层或更新文本图层前，对文本图层数据的额外操作
         *
         * - 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         */
        onBeforeAddOrUpdate(layer) {
            const layerData = layer.data as ITextLayerProps;

            // 过滤特殊字符
            if (layerData.text) {
                layerData.text = filterSpecialChars(layerData.text);
            }

            const runtimeData = layer.__runtime || {};
            if (!runtimeData.isInit) {
                // [2025-08-18 patch] 兼容 iOS bug: iOS 在 7.17.0 版本中，将文本图层中 rotate 保存为字符串或者为null，导致在 web 端无法正确显示
                if (typeof layer.rotate === 'string' || layer.rotate == null) {
                    let rotate = parseInt(layer.rotate, 10);
                    if (Number.isNaN(rotate)) rotate = 0;
                    layer.rotate = rotate;
                }

                // [2025-10-21 patch] 兼容在移动端使用无效花字素材的问题
                if (layerData.advancedTextMaterial) {
                    const { advancedTextMaterial } = layerData;
                    if (String(advancedTextMaterial.id) === '0' || advancedTextMaterial.material == null) {
                        delete layerData.advancedTextMaterial;

                        if (layer.materialIds && layer.materialIds[MaterialIdsType.ADVANCED_TEXT_MATERIAL]) {
                            delete layer.materialIds[MaterialIdsType.ADVANCED_TEXT_MATERIAL];
                        }
                    }
                }

                layer.data = transformLowLeverTextDataToCss(layer.data as ITextLayerProps);
            }

            // 若未加载列表序号字体，尝试先将字体全集设置到 __listMarkerFontFamily
            const { listStyleMaterial } = layerData;
            const needSetListOrderFontFamily =
                listStyleMaterial &&
                listStyleMaterial.fontId &&
                listStyleMaterial.autoIncrement !== 'none' &&
                !layer.__listMarkerFontFamily;

            if (needSetListOrderFontFamily) {
                const fontId = listStyleMaterial.fontId;
                if (fontId) {
                    const font = getFramework(identity).service.font.getById(fontId);
                    if (font) layer.__listMarkerFontFamily = font.name;
                }
            }

            layer.__runtime = { ...runtimeData, isInit: true };
        },
    };

    return behaviors;
};
